import { createCheck } from 'admin/actions/checks';
import { fetchOrganizations } from 'admin/actions/organization';
import { fetchShopAccounts, fetchShops } from 'admin/actions/shops';
import { Button } from 'admin/components/atoms/Buttons';
import ConfirmModal from 'admin/components/atoms/ConfirmModal';
import DateTime from 'admin/components/atoms/DateTime';
import Label from 'admin/components/atoms/Label';
import Labeled from 'admin/components/atoms/Labeled';
import Select from 'admin/components/atoms/Select';
import TextArea from 'admin/components/atoms/TextArea';
import Tooltip from 'admin/components/atoms/Tooltip';
import ApiResults from 'admin/components/molecules/ApiResults';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import { applyToTargetValue, formatDate, removeEmptyKeys, toQueryParams } from 'admin/utils';
import { toPointExpiresAtFormValue, toPointExpiresAtParam } from 'admin/utils/pointExpiry';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import { useAppSelector } from '../../../hooks/useApp';
import { selectUserPrivateMoney } from '../../../reducers/user-private-money';
import CopyToClipboard from '../../atoms/CopyToClipboard';
import DateTimeRange from '../../atoms/DateTimeRange';
import LocalizedMask from '../../atoms/LocalizedMask';
import NumberInput from '../../atoms/NumberInput';
import Required from '../../atoms/Required';
import QrCodeDrawer from '../../molecules/QrCodeDrawer';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';

const DummyQrCode = styled.div``;
const FormWrapper = styled.div`
  ${Labeled} {
    display: grid;
    grid-template-columns: 1.5fr 4fr;
    justify-items: left;
    margin-top: 10px;
    align-items: flex-start;
  }
  ${Labeled} > ${Label} {
    grid-column: 1;
    margin-top: 6px;
  }
  ${DateTime} {
    margin-bottom: 0;
  }
  ${Select} {
    width: 212px;
  }
  ${TextArea} {
    width: 500px;
    resize: both;
    margin-bottom: 10px;
  }
`;

const ModalWrapper = styled.div`
  ${Labeled} {
    display: grid;
    grid-template-columns: 1fr 4fr;
    grid-template-rows: auto;
    justify-items: left;
    margin-top: 10px;
  }

  ${Labeled} > ${Label} {
    grid-column: 1;
  }

  max-height: calc(100vh - 210px);
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-track {
    background: #fff;
    border-left: solid 1px #ececec;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
    box-shadow: inset 0 0 0 2px #fff;
  }

  ${TextArea} {
    width: 500px;
    resize: both;
    margin-bottom: 10px;
  }
`;

const SelectorsWrapper = styled.div`
  display: flex;
  gap: 16px;
`;

const SerialWrapper = styled.div`
  display: flex;
  gap: 12px;
  margin: 10px;
`;

const InputRow = styled.div`
  display: flex;
  gap: 16px;
  ${NumberInput}, ${DateTime} {
    width: 250px;
    margin-bottom: 0;
  }
`;

const VerticalLabeled = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const DistributionLabeled = styled(Labeled)`
  place-items: start left !important;
`;

const DistributionFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const FieldLabel = styled.div`
  margin-bottom: 4px;
`;

const ModalButtonsWrapper = styled.div`
  margin-top: 0.5rem;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
`;

const InfoText = styled.span`
  ${({ theme }) => css`
    ${theme.typography.body2.regular}
    color: ${theme.colors.gray[300]};
  `}
`;

const compareByName = (o1, o2) => o1.name.localeCompare(o2.name);

const CreateChecks = ({ user, className }) => {
  const [moneyAmount, setMoneyAmount] = useState(0);
  const [pointAmount, setPointAmount] = useState(0);
  const [usageLimit, setUsageLimit] = useState(1);
  const [pointExpiresIn, setPointExpiresIn] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedShopOption, setSelectedShopOption] = useState(null);
  const [note, setNote] = useState(null);
  const [selectedPointBearerOption, setSelectedPointBearerOption] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: moment().startOf('day'),
    to: moment().add(3, 'months').endOf('day'),
  });
  const [pointExpiresAt, setPointExpiresAt] = useState(null);
  const [description, setDescription] = useState(null);
  const [selectedShopAccount, setSelectedShopAccount] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [serialCode, setSerialCode] = useState('');
  const [qrSize, setQrSize] = useState(128);
  const [shopErrors, setShopErrors] = useState(null);
  const [pointBearerErrors, setPointBearerErrors] = useState(null);
  const [selectedPointBearerAccount, setSelectedPointBearerAccount] = useState(null);
  const [issuerShopName, setIssuerShopName] = useState(null);
  const [apiMessages, setApiMessages] = useState(null);
  const [pointBearerShops, setPointBearerShops] = useState([]);
  const [shops, setShops] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [selectedOrganizationOption, setSelectedOrganizationOption] = useState(null);
  const [selectedPointBearerOrganizationOption, setSelectedPointBearerOrganizationOption] =
    useState(null);
  const [showOrgSelectors, setShowOrgSelectors] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);

  const datetimeFormat = 'YYYY/MM/DD HH:mm:ss';
  const dateRangeFormat = 'YYYY/MM/DD HH:mm';

  const labelByName = (obj) => ({ value: obj, label: obj.name });

  const maybeLabel = (obj) => (obj && labelByName(obj)) || null;

  const crumbs = [
    { value: 'ホーム', href: '/', isCurrentPage: false },
    { value: '販促管理', href: '', isCurrentPage: true },
    { value: 'チャージQRコード管理', href: '/promotion/checks', isCurrentPage: false },
    { value: 'チャージQRコード新規発行', href: '/promotion/checks/create', isCurrentPage: true },
  ];

  const refreshShop = async (shop, money, setAcc, setErr) => {
    const accounts = await fetchShopAccounts(
      shop.id,
      toQueryParams({
        private_money_id: userPrivateMoney.id,
      })
    );
    const selectedAccount = accounts.find((a) => a.money_name === money.name);
    setAcc(selectedAccount);
    if (!selectedAccount) {
      setErr(['マネーが変更されました、店舗を選択し直してください']);
    } else {
      setErr([]);
    }
  };

  const handleShopChange = (opt) =>
    refreshShop(opt.value, userPrivateMoney, setSelectedShopAccount, setShopErrors);

  const handlePointBearerChange = (opt) =>
    refreshShop(opt.value, userPrivateMoney, setSelectedPointBearerAccount, setPointBearerErrors);

  const useEffectAsync = (f, params) =>
    useEffect(() => {
      f();
    }, params);

  const clearSelector = () => {
    setSelectedOrganizationOption(null);
    setSelectedShopOption(null);
    setSelectedShopAccount(null);
    setSelectedPointBearerOrganizationOption(null);
    setSelectedPointBearerAccount(null);
    setSelectedPointBearerOption(null);
  };

  useEffectAsync(async () => {
    clearSelector();
    if (userPrivateMoney.type !== 'own') {
      const fetchedOrgs = (
        await fetchOrganizations(
          toQueryParams({
            per_page: 10000,
            private_money_id: userPrivateMoney.id,
          })
        )
      ).rows;
      const orgs = fetchedOrgs.find((o) => o.code === userPrivateMoney.organization.code)
        ? fetchedOrgs
        : [user.organization].concat(fetchedOrgs);
      setOrganizations(orgs);
      setShowOrgSelectors(true);
    } else {
      setShowOrgSelectors(false);
      setSelectedOrganizationOption(maybeLabel(userPrivateMoney.organization));
      setSelectedPointBearerOrganizationOption(maybeLabel(userPrivateMoney.organization));
    }
  }, [userPrivateMoney]);

  useEffectAsync(async () => {
    setSelectedShopOption(null);
    setSelectedShopAccount(null);
    if (!selectedOrganizationOption) return;
    const localShops = await fetchShops(
      toQueryParams({
        organization_code: selectedOrganizationOption.value.code,
        private_money_id: userPrivateMoney.id,
      })
    );
    setShops(localShops.sort(compareByName));
  }, [selectedOrganizationOption]);

  useEffectAsync(async () => {
    if (!selectedPointBearerOrganizationOption) return;
    const localShops = await fetchShops(
      toQueryParams({
        organization_code: selectedPointBearerOrganizationOption.value.code,
        private_money_id: userPrivateMoney.id,
      })
    );
    setPointBearerShops(localShops.sort(compareByName));
  }, [selectedPointBearerOrganizationOption]);

  useEffect(() => {
    if (selectedPointBearerOption) {
      handlePointBearerChange(selectedPointBearerOption);
    }
  }, [selectedPointBearerOption]);

  useEffect(() => {
    if (selectedShopOption) {
      handleShopChange(selectedShopOption);
    }
  }, [selectedShopOption]);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const onConfirm = async () => {
    const account_id = selectedShopAccount && selectedShopAccount.id;
    const bear_point_account = selectedPointBearerAccount && selectedPointBearerAccount.id;
    try {
      const check = await createCheck(
        removeEmptyKeys({
          money_amount: moneyAmount,
          point_amount: pointAmount,
          note,
          account_id,
          bear_point_account,
          description,
          // is_onetime is a legacy attribute that will set to true by default which will cause sending usage limit invalid
          is_onetime: false,
          starts_at: dateRange.from ? moment(dateRange.from) : null,
          expires_at: dateRange.to ? moment(dateRange.to) : null,
          point_expires_at: pointExpiresAt ? moment(pointExpiresAt) : pointExpiresAt,
          point_expires_in_days: pointExpiresIn >= 0 ? pointExpiresIn : null,
          usage_limit: usageLimit ? Number(usageLimit) : null,
        })
      );
      setQrUrl(check.token);
      setSerialCode(check.serial_code);
      setIssuerShopName(selectedShopOption.value.name);
      setApiMessages([{ text: 'チャージQRコードは正常に生成されました' }]);
      setApiError(null);
    } catch (err) {
      const error = await err;
      // 失敗時に前回の発行結果が残っていると、新しく発行されたものと誤認されるため消す
      setQrUrl(null);
      setSerialCode('');
      setApiMessages([{ text: error.message, isError: true }]);
      setApiError(error);
    }
    closeModal();
  };

  const issueCheck = async (e) => {
    e.preventDefault();

    if (moneyAmount <= 0 && pointAmount <= 0) {
      setApiError({
        type: 'invalid_parameters',
        errors: {
          missing: ['amount'],
        },
      });
      return;
    }

    setApiError(null);

    openModal();
  };

  const isMissingParameter = (name) =>
    apiError &&
    apiError.type === 'invalid_parameters' &&
    apiError.errors.missing &&
    apiError.errors.missing.includes(name);

  const isInvalidParameter = (name) =>
    apiError &&
    apiError.type === 'invalid_parameters' &&
    apiError.errors.invalid &&
    apiError.errors.invalid.includes(name);

  const shopOptions = shops.map(labelByName);
  const pointBearerShopOptions = pointBearerShops.map(labelByName);

  const organizationOptions = organizations.map(labelByName);

  const labelWithHelp = (toolTipId, labelText, helpText) => (
    <div>
      {labelText}
      <Tooltip id={toolTipId} text={helpText} />
    </div>
  );

  return (
    <div className={className}>
      <BreadCrumb crumbs={crumbs} />
      <ApiResults messages={apiMessages} />
      <FormWrapper>
        <Labeled labelText="チャージ元店舗" required>
          <VerticalLabeled>
            <SelectedPrivateMoneyDisplay labelTextStyles={{ marginTop: 0 }} />
            <SelectorsWrapper>
              {showOrgSelectors ? (
                <Select
                  options={organizationOptions}
                  value={selectedOrganizationOption}
                  setSelected={setSelectedOrganizationOption}
                  placeholder="「組織」を選択"
                />
              ) : null}
              <Select
                options={shopOptions}
                value={selectedShopOption}
                setSelected={setSelectedShopOption}
                errors={shopErrors}
                placeholder="「店舗」を選択"
              />
            </SelectorsWrapper>
          </VerticalLabeled>
        </Labeled>
        <Labeled
          labelText={labelWithHelp(
            'helpNote',
            '用途',
            <div>複数QRコードを発行した場合に用途（32文字以内）を記入します</div>
          )}
        >
          <TextArea
            value={note !== null ? note : ''}
            onChange={applyToTargetValue(setNote)}
            maxLength={32}
            rows={1}
          />
        </Labeled>
        <Labeled labelText="金額" required>
          <div>
            <InputRow>
              <VerticalLabeled>
                <span>マネー</span>
                <LocalizedMask value={moneyAmount} onChange={(e) => setMoneyAmount(e.target.value)}>
                  {(inputProps) => <NumberInput {...inputProps} min="0" />}
                </LocalizedMask>
              </VerticalLabeled>
              <VerticalLabeled>
                <span>ポイント</span>
                <LocalizedMask value={pointAmount} onChange={(e) => setPointAmount(e.target.value)}>
                  {(inputProps) => <NumberInput {...inputProps} min="0" />}
                </LocalizedMask>
              </VerticalLabeled>
            </InputRow>
            <InfoText>マネーあるいはポイントいずれかの入力が必須です</InfoText>
          </div>
        </Labeled>
        <Labeled
          labelText={labelWithHelp(
            'helpPointExpires',
            'ポイント有効期限',
            <div>
              未指定の場合はマネー有効期限と同一の有効期限が設定されます。
              <br />
              日付指定と相対日数指定が両方指定されている場合は、受け取り時点でより近い方が有効期限に設定されます。
            </div>
          )}
        >
          <InputRow>
            <VerticalLabeled>
              <span>日時指定</span>
              <DateTime
                dateFormat="YYYY/MM/DD HH:mm:ss"
                timeFormat={false}
                value={toPointExpiresAtFormValue(pointExpiresAt)}
                onChange={(date) => setPointExpiresAt(toPointExpiresAtParam(date))}
                id="check_point_expires_at_date_id"
                validType="validAfterToday"
                errors={[isInvalidParameter('point_expires_at') && 'Invalid date']}
                showIcon
              />
            </VerticalLabeled>
            <VerticalLabeled>
              <span>相対日数指定</span>
              <NumberInput
                value={pointExpiresIn || ''}
                setValue={(v) => setPointExpiresIn(v && v >= 0 ? v : null)}
                placeholder="なし"
                min="0"
              />
              <span style={{ color: '#757575', marginTop: '0.25rem' }}>
                0にすると当日の24時までになります
              </span>
            </VerticalLabeled>
          </InputRow>
        </Labeled>
        <Labeled
          labelText={labelWithHelp(
            'helpPointBearerOrgShop',
            'ポイント負担先店舗',
            <span>
              ポイント利用分をマネー発行体に支払う店舗を指定します。
              <br />
              未指定時はマネー発行者が負担します。
            </span>
          )}
        >
          <SelectorsWrapper>
            {showOrgSelectors ? (
              <Select
                options={organizationOptions}
                value={selectedPointBearerOrganizationOption}
                setSelected={setSelectedPointBearerOrganizationOption}
                placeholder="「組織」を選択"
              />
            ) : null}
            <Select
              options={pointBearerShopOptions}
              value={selectedPointBearerOption}
              setSelected={setSelectedPointBearerOption}
              errors={pointBearerErrors}
              placeholder="「店舗」を選択"
            />
          </SelectorsWrapper>
        </Labeled>
        <Labeled
          labelText={labelWithHelp(
            'helpDescription',
            '説明文',
            <div>
              QRコードと共にお客様に表示するメッセージ（200文字まで）です。
              <br />
              取引詳細の説明文にも表示します。
              <br />
            </div>
          )}
        >
          <TextArea
            value={description !== null ? description : ''}
            onChange={applyToTargetValue(setDescription)}
          />
        </Labeled>
        <DistributionLabeled labelText="配布設定">
          <DistributionFields>
            <VerticalLabeled>
              <FieldLabel>
                読取可能期間 <Required />
              </FieldLabel>
              <DateTimeRange
                dateRange={dateRange}
                setDateRange={setDateRange}
                validTypeTo="invalidFromBeforeDay"
                dateTimeProps={{
                  fromProps: { showIcon: true, timeFormat: 'HH:mm' },
                  toProps: { showIcon: true, timeFormat: 'HH:mm' },
                }}
              />
              {(!dateRange.from || !dateRange.to) && (
                <span style={{ color: '#e53935', marginTop: '0.25rem' }}>
                  読取可能期間の開始日時と終了日時を指定して下さい
                </span>
              )}
              {isInvalidParameter('expires_at') && (
                <span style={{ color: '#e53935', marginTop: '0.25rem' }}>Invalid date</span>
              )}
              {apiError && apiError.type === 'invalid_parameter_expires_at' && (
                <span style={{ color: '#e53935', marginTop: '0.25rem' }}>{apiError.message}</span>
              )}
            </VerticalLabeled>
            <VerticalLabeled>
              <FieldLabel>
                {labelWithHelp(
                  'helpUsageLimit',
                  '利用人数',
                  'お客様1人につき利用回数は1回のみです'
                )}
              </FieldLabel>
              <NumberInput
                value={usageLimit || ''}
                setValue={(v) => setUsageLimit(v && v > 0 ? v : null)}
                placeholder="上限なし"
                min="0"
              />
            </VerticalLabeled>
          </DistributionFields>
        </DistributionLabeled>
        <Button
          onClick={issueCheck}
          disabled={!selectedShopAccount || !dateRange.from || !dateRange.to}
        >
          発行
        </Button>

        {qrUrl ? (
          <Labeled labelText="配布物">
            <div>
              <QrCodeDrawer
                uri={qrUrl}
                name={issuerShopName}
                qrSize={qrSize}
                showUrl
                urlAsLabel
                copyable
              />
              {serialCode && (
                <SerialWrapper>
                  <Label>{serialCode}</Label>
                  <CopyToClipboard text={serialCode} tooltipId="serialCode" />
                </SerialWrapper>
              )}
            </div>
          </Labeled>
        ) : (
          <DummyQrCode>ここにQRコードが表示されます</DummyQrCode>
        )}
      </FormWrapper>
      <ConfirmModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        title="以下の内容でチャージQRを発行します"
        form={
          <ModalWrapper>
            <Labeled labelText="マネー">{userPrivateMoney.name}</Labeled>
            <Labeled labelText="チャージ元組織">
              {selectedOrganizationOption !== null ? selectedOrganizationOption.label : null}
            </Labeled>
            <Labeled labelText={<div style={{ wordBreak: 'keep-all' }}>チャージ元店舗</div>}>
              <div style={{ wordBreak: 'break-word' }}>
                {selectedShopOption !== null ? selectedShopOption.label : null}
              </div>
            </Labeled>
            <Labeled labelText="用途">{note}</Labeled>
            <Labeled labelText="マネー">{moneyAmount}</Labeled>
            <Labeled labelText="ポイント">{pointAmount}</Labeled>
            <Labeled labelText="ポイント有効期限(日付指定)">
              {pointExpiresAt
                ? formatDate(toPointExpiresAtFormValue(pointExpiresAt), datetimeFormat)
                : null}
            </Labeled>
            <Labeled labelText="ポイント有効期限(相対日数指定)">{pointExpiresIn}</Labeled>
            <Labeled labelText="ポイント負担先組織">
              {selectedPointBearerOrganizationOption !== null
                ? selectedPointBearerOrganizationOption.label
                : null}
            </Labeled>
            <Labeled labelText={<div style={{ wordBreak: 'keep-all' }}>ポイント負担先店舗</div>}>
              <div style={{ wordBreak: 'break-word' }}>
                {selectedPointBearerOption !== null ? selectedPointBearerOption.label : null}
              </div>
            </Labeled>
            <Labeled labelText="説明文">{description}</Labeled>
            <Labeled labelText="読取可能期間">
              {dateRange.from ? formatDate(dateRange.from, dateRangeFormat) : null}
              {' 〜 '}
              {dateRange.to ? formatDate(dateRange.to, dateRangeFormat) : null}
            </Labeled>
            <Labeled labelText="利用人数">{usageLimit !== null ? usageLimit : '上限なし'}</Labeled>
            <ModalButtonsWrapper>
              <Button variant="neutral" onClick={closeModal}>
                キャンセル
              </Button>
              <Button variant="contained" onClick={onConfirm}>
                実行
              </Button>
            </ModalButtonsWrapper>
          </ModalWrapper>
        }
      />
    </div>
  );
};

CreateChecks.defaultProps = {
  className: '',
};

CreateChecks.propTypes = {
  user: PropTypes.object.isRequired,
  className: PropTypes.string,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(styled(CreateChecks)`
  ${DummyQrCode} {
    margin-top: 10px;
    width: 200px;
    height: 200px;
    background: lightGray;
  }
  ${Select} {
    width: 250px;
  }
`);
